import { DatePipe } from '@angular/common';

/// PrimeHat's clock (the owner, 2026-11-03): every time on every page is Bangladesh time, UTC+6 all year (no daylight
/// saving), whatever clock the viewer's computer or the server's renderer keeps. The API's times are UTC.
export const SITE_TIME_ZONE = '+0600';
export const SITE_TIME_ZONE_NAME = 'Asia/Dhaka';
const SITE_OFFSET_MS = 6 * 60 * 60 * 1000;

const NO_ZONE = /^\d{4}-\d\d-\d\dT\d\d:\d\d(:\d\d(\.\d+)?)?$/;
const DAY_ONLY = /^\d{4}-\d\d-\d\d$/;

/// A time from the API as a moment. The API marks its times UTC ("...Z"); one without a mark (an older cached answer)
/// is UTC too, and a bare day ("2026-11-03") is that calendar day.
export function serverTime(value: string | Date | number | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') { return null; }
    if (value instanceof Date) { return isNaN(value.getTime()) ? null : value; }
    if (typeof value === 'number') { return new Date(value); }
    const text = String(value).trim();
    const date = new Date(NO_ZONE.test(text) ? text + 'Z' : DAY_ONLY.test(text) ? text + 'T00:00:00Z' : text);
    return isNaN(date.getTime()) ? null : date;
}

/// Bangladesh's wall clock at a moment, as a Date whose UTC fields (getUTCHours, getUTCDate...) read it.
export function siteClock(at: Date | number = Date.now()): Date {
    return new Date((typeof at === 'number' ? at : at.getTime()) + SITE_OFFSET_MS);
}

/// The Bangladesh day of a moment, "yyyy-MM-dd".
export function siteDay(at: Date | number = Date.now()): string {
    return siteClock(at).toISOString().slice(0, 10);
}

/// The hour (0-23) in Bangladesh now.
export function siteHour(): number {
    return siteClock().getUTCHours();
}

/// The moment a Bangladesh wall-clock time names (month from 0, as Date's).
export function fromSiteClock(year: number, month: number, day: number, hour = 0, minute = 0): Date {
    return new Date(Date.UTC(year, month, day, hour, minute) - SITE_OFFSET_MS);
}

/// "2026-11-03T17:52" in Bangladesh time, for <input type="datetime-local">.
export function siteInputValue(at: Date | null): string {
    if (!at || isNaN(at.getTime())) { return ''; }
    return siteClock(at).toISOString().slice(0, 16);
}

/// An <input type="datetime-local"> value read as Bangladesh time.
export function fromSiteInputValue(value: string): Date | null {
    const m = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d)/.exec(value || '');
    return m ? fromSiteClock(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]) : null;
}

// The date pipe on every page ({{ x | date:'h:mm a' }}) says Bangladesh time unless a template names a zone (a bare day
// shown with 'UTC', say). Patched once, here, when the app module loads - in the browser and in the server's renderer alike.
const datePipeTransform = DatePipe.prototype.transform as (...args: any[]) => any;
(DatePipe.prototype as any).transform = function (this: DatePipe, value: any, format?: string, timezone?: string, locale?: string): string | null {
    if (!timezone && typeof value === 'string') {
        const text = value.trim();
        if (DAY_ONLY.test(text)) { return datePipeTransform.call(this, text + 'T00:00:00Z', format, 'UTC', locale); }
        if (NO_ZONE.test(text)) { value = text + 'Z'; }
    }
    return datePipeTransform.call(this, value, format, timezone || SITE_TIME_ZONE, locale);
};
